import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import {
  ActivityLogItemApiResponse,
  ActivityLogItemCreateRequest,
  ActivityLogItemFilters,
  ActivityLogItemQueryParams,
  ActivityLogItemUpdateRequest,
} from '../../entities/activity-tracking/activity-log-item-api.types';
import { ActivityLogItem } from '../../entities/activity-tracking/activity-log-item.entities';
import { EntityCreatedResponse, PaginatedResponse } from '../../models/api/api-response.model';
import { BaseApiService } from '../api/base-api.service';

@Injectable({
  providedIn: 'root'
})
export class ActivityLogService extends BaseApiService {

  getActivityLogs(params?: ActivityLogItemQueryParams, filters?: ActivityLogItemFilters): Observable<PaginatedResponse<ActivityLogItem>> {
    // Get farm_id from filters - it's required for the path
    const farmId = filters?.farm_id;
    if (!farmId) {
      throw new Error('Farm ID is required to get activity logs');
    }

    let httpParams = new HttpParams();

    // Add required search parameter (API requires 'q' parameter)
    if (params?.q && params.q.trim() !== '') {
      httpParams = httpParams.set('q', params.q.trim());
    }

    if (params?.skip !== undefined) httpParams = httpParams.set('skip', params.skip.toString());
    if (params?.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.sort) httpParams = httpParams.set('sort', params.sort);
    if (params?.order) httpParams = httpParams.set('order', params.order);

    // Add other filters as query parameters (excluding farm_id which goes in path)
    if (filters?.user_id) httpParams = httpParams.set('_user_id', filters.user_id);
    if (filters?.module) httpParams = httpParams.set('_module', filters.module);

    const endpoint = `/farms/${farmId}/activity-tracking/logs`;
    return this.getPaginated<ActivityLogItem>(endpoint, httpParams);
  }

  getActivityLogById(id: string, farmId: string): Observable<ActivityLogItemApiResponse> {
    return this.get<ActivityLogItemApiResponse>(`/farms/${farmId}/activity-tracking/logs/${id}`);
  }

  createActivityLog(log: ActivityLogItemCreateRequest, farmId: string): Observable<EntityCreatedResponse> {
    return this.post<EntityCreatedResponse>(`/farms/${farmId}/activity-tracking/logs`, log);
  }

  updateActivityLog(id: string, updates: ActivityLogItemUpdateRequest, farmId: string): Observable<void> {
    return this.put<void>(`/farms/${farmId}/activity-tracking/logs/${id}`, updates);
  }

  patchActivityLog(id: string, updates: Partial<ActivityLogItemUpdateRequest>, farmId: string): Observable<void> {
    return this.patch<void>(`/farms/${farmId}/activity-tracking/logs/${id}`, updates);
  }

  deleteActivityLog(id: string, farmId: string): Observable<void> {
    return this.delete<void>(`/farms/${farmId}/activity-tracking/logs/${id}`);
  }

  getActivitiesByFarm(farmId: string, params?: ActivityLogItemQueryParams): Observable<PaginatedResponse<ActivityLogItem>> {
    return this.getActivityLogs(params, { farm_id: farmId });
  }

  getActivitiesByUser(userId: string, params?: ActivityLogItemQueryParams): Observable<PaginatedResponse<ActivityLogItem>> {
    return this.getActivityLogs(params, { user_id: userId });
  }

  getActivitiesByModule(module: string, params?: ActivityLogItemQueryParams): Observable<PaginatedResponse<ActivityLogItem>> {
    return this.getActivityLogs(params, { module });
  }
}
